/**
 * OverviewAudio — floating bottom-right card that pops in shortly after the
 * proposal loads (with a soft "pop" chime) and plays a short voiceover
 * introducing Social Go & Social Pro. Play / pause / dismiss only.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Headphones, Pause, Play, RotateCcw, X } from 'lucide-react';
import './OverviewAudio.css';

export const OVERVIEW_AUDIO_URL = 'https://nichecomfiles.co.uk/proposal-examples/defaults/social-go-pro-intro.mp3';
const APPEAR_DELAY_MS = 1800;

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

// Soft two-tone "pop" synthesised with Web Audio — no asset, ~180ms.
function playPop() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return Promise.reject(new Error('no-audio'));
  const ctx = new Ctx();
  return ctx.resume().then(() => {
    if (ctx.state !== 'running') throw new Error('blocked');
    const t0 = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, t0);
    master.gain.exponentialRampToValueAtTime(0.22, t0 + 0.015);
    master.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.24);
    master.connect(ctx.destination);
    [[660, 0], [990, 0.07]].forEach(([freq, dt]) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, t0 + dt);
      osc.frequency.exponentialRampToValueAtTime(freq * 0.82, t0 + dt + 0.16);
      osc.connect(master);
      osc.start(t0 + dt);
      osc.stop(t0 + dt + 0.2);
    });
    setTimeout(() => ctx.close().catch(() => {}), 600);
  });
}

export default function OverviewAudio({ src = OVERVIEW_AUDIO_URL, label = 'Hear the overview', sub = 'A 1-minute intro to Social Go & Social Pro' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [failed, setFailed] = useState(false);
  const popped = useRef(false);

  // Pop in after a beat; chime if the browser allows sound before interaction,
  // otherwise chime on the visitor's first tap/keypress instead.
  useEffect(() => {
    const show = setTimeout(() => {
      setVisible(true);
      playPop().then(() => { popped.current = true; }).catch(() => {});
    }, APPEAR_DELAY_MS);
    const onFirstGesture = () => {
      if (!popped.current) { popped.current = true; playPop().catch(() => {}); }
      cleanup();
    };
    const cleanup = () => {
      window.removeEventListener('pointerdown', onFirstGesture, true);
      window.removeEventListener('keydown', onFirstGesture, true);
    };
    const arm = setTimeout(() => {
      if (popped.current) return;
      window.addEventListener('pointerdown', onFirstGesture, true);
      window.addEventListener('keydown', onFirstGesture, true);
    }, APPEAR_DELAY_MS + 300);
    return () => { clearTimeout(show); clearTimeout(arm); cleanup(); };
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onTime = () => setTime(el.currentTime);
    const onMeta = () => setDuration(el.duration || 0);
    const onEnd = () => { setPlaying(false); setEnded(true); };
    const onPause = () => setPlaying(false);
    const onPlay = () => { setPlaying(true); setEnded(false); };
    const onErr = () => setFailed(true);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('ended', onEnd);
    el.addEventListener('pause', onPause);
    el.addEventListener('play', onPlay);
    el.addEventListener('error', onErr);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('ended', onEnd);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('error', onErr);
    };
  }, []);

  const toggle = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    popped.current = true;
    if (playing) { el.pause(); return; }
    if (ended) el.currentTime = 0;
    el.play().catch(() => setFailed(true));
  }, [playing, ended]);

  const dismiss = () => { ref.current?.pause(); setDismissed(true); };

  if (failed || dismissed) return null;
  const pct = duration ? Math.min(100, (time / duration) * 100) : 0;
  const Icon = playing ? Pause : ended ? RotateCcw : Play;
  const title = playing ? 'Playing the overview' : ended ? 'Hear it again' : time > 0 ? 'Resume the overview' : label;
  const status = playing || time > 0 ? `${fmt(time)} / ${fmt(duration)}` : sub;

  return (
    <div className={`ova ${visible ? 'is-visible' : ''} ${playing ? 'is-playing' : ''}`} role="complementary" aria-label="Overview voiceover" data-testid="overview-audio">
      <audio ref={ref} src={src} preload="metadata" />
      <button type="button" className="ova-close" onClick={dismiss} aria-label="Dismiss" data-testid="overview-audio-dismiss"><X size={16} /></button>
      <div className="ova-row">
        <button type="button" className="ova-play" onClick={toggle} aria-pressed={playing} aria-label={playing ? 'Pause overview' : 'Play overview'} data-testid="overview-audio-toggle">
          <Icon size={26} strokeWidth={2.4} />
        </button>
        <div className="ova-text">
          <span className="ova-eyebrow"><Headphones size={13} /> Voiceover</span>
          <span className="ova-title" data-testid="overview-audio-title">{title}</span>
          <span className="ova-sub" data-testid="overview-audio-status">{status}</span>
        </div>
        <span className="ova-bars" aria-hidden="true"><i /><i /><i /><i /></span>
      </div>
      <div className="ova-bar" aria-hidden="true"><div className="ova-fill" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}
